import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminScopesApi, RequestError } from '../api/client';
import { scopeInfoFromMe, UNKNOWN_SCOPE, type AdminScopeInfo, type AdminScopeStatus } from '../utils/adminAccess';

/*
 * ログイン中の管理者の閲覧範囲（課題 #100）。
 *
 * GET /v1/admin/admin-scopes/me（BE PR #124）で、全社管理者か拠点管理者かと、
 * 拠点管理者の閲覧範囲の部署を得る。/me が無い古い BE では、全社管理者専用の
 * 管理者一覧（GET /v1/admin/admin-scopes）が 403 を返すかで見分ける（部署は分からない）。
 * ナビゲーションは画面を移るたびに描き直されるので、利用者ごとに 1 回だけ問い合わせる。
 */
let cache: { userId: string; promise: Promise<AdminScopeInfo> } | null = null;

const LOADING: AdminScopeInfo = { ...UNKNOWN_SCOPE, status: 'loading' };

async function fromLegacyList(token: string): Promise<AdminScopeInfo> {
  try {
    await adminScopesApi.list(token);
    return { status: 'unrestricted', departmentIds: [], departmentNames: [], seesNothing: false, userDeletion: 'unknown' };
  } catch (err) {
    if (err instanceof RequestError && err.status === 403) {
      return { status: 'restricted', departmentIds: null, departmentNames: null, seesNothing: false, userDeletion: 'unknown' };
    }
    throw err;
  }
}

function resolveScope(userId: string, token: string): Promise<AdminScopeInfo> {
  if (cache && cache.userId === userId) return cache.promise;
  const promise = adminScopesApi
    .me(token)
    .then(scopeInfoFromMe)
    .catch((err) => {
      if (err instanceof RequestError && err.status === 404) return fromLegacyList(token);
      throw err;
    })
    .catch((): AdminScopeInfo => {
      // 一時的な失敗は次の画面でもう一度確かめられるよう、覚えない
      cache = null;
      return UNKNOWN_SCOPE;
    });
  cache = { userId, promise };
  return promise;
}

/** テスト用。ログアウト時にも呼ぶ */
export function resetAdminScopeCache(): void {
  cache = null;
}

export function useAdminScopeInfo(): AdminScopeInfo {
  const { user, getToken } = useAuth();
  const isAdmin = user?.role === 'admin';
  const userId = user?.userId ?? '';
  const [info, setInfo] = useState<AdminScopeInfo>(isAdmin ? LOADING : UNKNOWN_SCOPE);

  useEffect(() => {
    if (!isAdmin || !userId) {
      setInfo(UNKNOWN_SCOPE);
      return;
    }
    const token = getToken();
    if (!token) {
      setInfo(UNKNOWN_SCOPE);
      return;
    }
    let active = true;
    resolveScope(userId, token).then((s) => {
      if (active) setInfo(s);
    });
    return () => {
      active = false;
    };
  }, [isAdmin, userId, getToken]);

  return info;
}

/** 全社管理者か拠点管理者かだけが要る場面で使う */
export function useAdminScope(): AdminScopeStatus {
  return useAdminScopeInfo().status;
}
